import { useGetPartnerPaymentMethodsQuery } from '@/features/means-of-payments/api/useGetPartnerPaymentMethodsQuery.ts';
import { useGetWireTransferSetupQuery } from '@/features/means-of-payments/api/useGetWireTransferSetupQuery.ts';
import { useCallback, useMemo } from 'react';

/**
 * useGetPaymentMethod hook.
 * Is centralizing the logic of deciding which is the mean of payment of a partner
 *
 * @returns an object containing methods to get the partner payment method
 */
export const useGetPaymentMethod = () => {
  const getWireTransferSetupQuery = useGetWireTransferSetupQuery();
  const getPartnerPaymentMethodsQuery = useGetPartnerPaymentMethodsQuery({
    paginate: {
      start: 0,
      page: 1,
      limit: 10,
    },
  });

  const currentMeanOfPayment = useMemo(() => {
    if (!getPartnerPaymentMethodsQuery.data) return undefined;

    if (getWireTransferSetupQuery.data !== undefined) {
      return getWireTransferSetupQuery.data;
    }

    if (getPartnerPaymentMethodsQuery.data.data.length > 0) {
      // in case we have at least one mean of payment, try to get the default one, otherwise, take the first of the list
      return (
        getPartnerPaymentMethodsQuery.data.data.find(
          (iteratedMeanOfPayment) => {
            return iteratedMeanOfPayment.isDefault;
          },
        ) ?? getPartnerPaymentMethodsQuery.data.data[0]
      );
    }

    return undefined;
  }, [getWireTransferSetupQuery, getPartnerPaymentMethodsQuery.data]);

  const handleRefetch = useCallback(() => {
    void getWireTransferSetupQuery.refetch();
    void getPartnerPaymentMethodsQuery.refetch();
  }, [getWireTransferSetupQuery, getPartnerPaymentMethodsQuery]);

  return {
    meanOfPayment: currentMeanOfPayment,
    error: getPartnerPaymentMethodsQuery.error,
    refetch: handleRefetch,
    // Reflect only the *initial* load, not background refetches. Folding in
    // isFetching makes this flip true whenever the shared getWireTransferSetup
    // cache entry is re-fetched — including the refetch triggered when our
    // WireTransferElement mounts as "wire transfer" is selected. Since
    // StripeSection unmounts the PaymentContextProvider while isLoading is true,
    // that refetch was tearing the provider down and resetting its internal
    // selectedPayment state back to 'card'.
    isLoading:
      getPartnerPaymentMethodsQuery.isLoading ||
      getWireTransferSetupQuery.isLoading,
  };
};
